import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, InputAdornment,
  Avatar, Chip, Card, CardContent, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, IconButton, CircularProgress, Divider, Tab, Tabs,
  FormControl, InputLabel, Select, MenuItem, Alert, Snackbar, Switch,
  FormControlLabel, Tooltip, List, ListItem, ListItemText, ListItemAvatar,
  Badge, Accordion, AccordionSummary, AccordionDetails, LinearProgress
} from '@mui/material';
import { 
  Search, Close, Person, AccountBalanceWallet, CheckCircle, Today,
  Add, Remove, Delete, Edit, Visibility, Refresh, Warning,
  AttachMoney, History, PersonOff, PersonOutline, Flight, Hotel,
  PhoneAndroid, Receipt, Download, Print, ExpandMore, FilterList,
  LocalGasStation, ElectricBolt, DirectionsCar, Tv
} from '@mui/icons-material';
import { api_urls } from '../../utils/api-urls';

const NewUsers = () => {
  // User list state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    usersWithWallet: 0,
    usersWithProfile: 0,
    todaySignups: 0
  });

  // Selected user state
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  
  // Wallet transaction state
  const [walletDialogOpen, setWalletDialogOpen] = useState(false);
  const [walletAmount, setWalletAmount] = useState('');
  const [walletType, setWalletType] = useState('CREDIT');
  const [walletNotes, setWalletNotes] = useState('');
  const [walletTransactionFor, setWalletTransactionFor] = useState('OTHER');
  const [walletLoading, setWalletLoading] = useState(false);
  
  // Comprehensive transaction history
  const [comprehensiveHistory, setComprehensiveHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all');
  
  // Invoice dialog
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Status update
  const [statusLoading, setStatusLoading] = useState(false);

  // Phone edit state
  const [phoneEditOpen, setPhoneEditOpen] = useState(false);
  const [editPhoneValue, setEditPhoneValue] = useState('');
  const [phoneEditLoading, setPhoneEditLoading] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Handle phone number update
  const handlePhoneUpdate = async () => {
    if (!editPhoneValue.trim() || editPhoneValue.trim().length < 10) {
      setSnackbar({ open: true, message: 'Please enter a valid 10-digit phone number', severity: 'error' });
      return;
    }
    try {
      setPhoneEditLoading(true);
      const { data } = await axios.post(api_urls + 'api/admin/admin-update-phone', {
        userId: selectedUser._id,
        newPhone: editPhoneValue.trim()
      });
      if (data.success) {
        setSnackbar({ open: true, message: data.message || 'Phone number updated successfully', severity: 'success' });
        setPhoneEditOpen(false);
        setSelectedUser({ ...selectedUser, phone: data.newPhone || editPhoneValue.trim() });
        // Update in users list too
        setUsers(prev => prev.map(u => u._id === selectedUser._id ? { ...u, phone: data.newPhone || editPhoneValue.trim() } : u));
      } else {
        setSnackbar({ open: true, message: data.message || 'Failed to update phone', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Error updating phone number', severity: 'error' });
    } finally {
      setPhoneEditLoading(false);
    }
  };

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${api_urls}api/app/customer/all-users?page=${page + 1}&limit=${rowsPerPage}&search=${search}&statFilter=${activeFilter}`
      );
      if (response.data.status) {
        setUsers(response.data.data);
        setTotalUsers(response.data.total);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setSnackbar({ open: true, message: 'Failed to fetch users', severity: 'error' });
    }
    setLoading(false);
  }, [page, rowsPerPage, search, activeFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Fetch comprehensive transaction history
  const fetchComprehensiveHistory = async (userId, type = 'all') => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(
        `${api_urls}api/app/customer/user-comprehensive-history/${userId}?page=1&limit=100&type=${type}`
      );
      if (response.data.success) {
        setComprehensiveHistory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching comprehensive history:', error);
      setSnackbar({ open: true, message: 'Failed to fetch transaction history', severity: 'error' });
    }
    setHistoryLoading(false);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  // Handle row click
  const handleRowClick = (user) => {
    setSelectedUser(user);
    setDialogTab(0);
    setDialogOpen(true);
    setHistoryFilter('all');
    fetchComprehensiveHistory(user._id, 'all');
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    const newFilter = event.target.value;
    setHistoryFilter(newFilter);
    if (selectedUser) {
      fetchComprehensiveHistory(selectedUser._id, newFilter);
    }
  };

  // Handle wallet transaction
  const handleWalletTransaction = async () => {
    if (!walletAmount || parseFloat(walletAmount) <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid amount', severity: 'error' });
      return;
    }

    setWalletLoading(true);
    try {
      const response = await axios.post(`${api_urls}api/app/customer/admin-wallet-transaction`, {
        userId: selectedUser._id,
        amount: parseFloat(walletAmount),
        type: walletType,
        notes: walletNotes,
        transactionFor: walletTransactionFor
      });

      if (response.data.success) {
        setSnackbar({ 
          open: true, 
          message: `Wallet ${walletType === 'CREDIT' ? 'credited' : 'debited'} successfully. New balance: ₹${response.data.data.newBalance}`,
          severity: 'success' 
        });
        setWalletDialogOpen(false);
        setWalletAmount('');
        setWalletNotes('');
        
        // Update selected user's wallet
        setSelectedUser(prev => ({ ...prev, wallet: response.data.data.newBalance }));
        
        // Refresh history
        fetchComprehensiveHistory(selectedUser._id, historyFilter);
        
        // Refresh users list
        fetchUsers();
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Transaction failed',
        severity: 'error' 
      });
    }
    setWalletLoading(false);
  };

  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    setStatusLoading(true);
    try {
      const response = await axios.post(`${api_urls}api/app/customer/update-user-status`, {
        userId: selectedUser._id,
        status: newStatus
      });

      if (response.data.success) {
        setSnackbar({ open: true, message: `User status updated to ${newStatus}`, severity: 'success' });
        setSelectedUser(prev => ({ ...prev, status: newStatus }));
        fetchUsers();
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
    setStatusLoading(false);
  };

  // Handle user delete
  const handleDeleteUser = async () => {
    setDeleteLoading(true);
    try {
      const response = await axios.post(`${api_urls}api/app/customer/update-user-status`, {
        userId: selectedUser._id,
        action: 'delete'
      });

      if (response.data.success) {
        setSnackbar({ open: true, message: 'User deactivated successfully', severity: 'success' });
        setDeleteDialogOpen(false);
        setDialogOpen(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete user', severity: 'error' });
    }
    setDeleteLoading(false);
  };

  // Show invoice dialog
  const handleShowInvoice = (transaction) => {
    setSelectedTransaction(transaction);
    setInvoiceDialogOpen(true);
  };

  // Print invoice
  const handlePrintInvoice = () => {
    window.print();
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'WALLET': return <AccountBalanceWallet sx={{ color: '#4caf50' }} />;
      case 'RECHARGE': return <PhoneAndroid sx={{ color: '#2196f3' }} />;
      case 'FLIGHT': return <Flight sx={{ color: '#ff9800' }} />;
      case 'HOTEL': return <Hotel sx={{ color: '#9c27b0' }} />;
      default: return <Receipt sx={{ color: '#757575' }} />;
    }
  };

  // Get service icon
  const getServiceIcon = (service) => {
    const serviceMap = {
      'MOBILE RECHARGE': <PhoneAndroid fontSize="small" />,
      'DTH': <Tv fontSize="small" />,
      'ELECTRICITY': <ElectricBolt fontSize="small" />,
      'GAS': <LocalGasStation fontSize="small" />,
      'FASTAG': <DirectionsCar fontSize="small" />,
      'FLIGHT_BOOKING': <Flight fontSize="small" />,
      'HOTEL_BOOKING': <Hotel fontSize="small" />,
      'WALLET_TOPUP': <Add fontSize="small" />,
    };
    return serviceMap[service] || <Receipt fontSize="small" />;
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'confirmed':
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // Stat Card Component
  const StatCard = ({ title, value, icon, color, onClick, active }) => (
    <Card onClick={onClick} sx={{ height: '100%', background: active ? `linear-gradient(135deg, ${color}30, ${color}15)` : `linear-gradient(135deg, ${color}15, ${color}05)`, border: active ? `2px solid ${color}` : `1px solid ${color}30`, cursor: onClick ? 'pointer' : 'default', transition: 'all 0.2s', '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: `0 4px 12px ${color}40` } : {} }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" variant="body2">{title}</Typography>
          <Typography variant="h4" sx={{ color, fontWeight: 'bold' }}>{value?.toLocaleString() || 0}</Typography>
        </Box>
        <Box sx={{ backgroundColor: `${color}20`, borderRadius: '50%', p: 1.5 }}>{icon}</Box>
      </CardContent>
    </Card>
  );

  // Invoice Component
  const InvoiceContent = ({ transaction, user }) => (
    <Box sx={{ p: 3 }} id="invoice-content">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, borderBottom: '2px solid #ff6600', pb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ color: '#ff6600', fontWeight: 'bold' }}>BHARAT DARSHAN</Typography>
          <Typography variant="body2" color="textSecondary">Travel & Services Platform</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>INVOICE</Typography>
          <Typography variant="body2">#{transaction?.invoiceNo}</Typography>
          <Typography variant="body2">{formatDate(transaction?.date)}</Typography>
        </Box>
      </Box>

      {/* Customer Details */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <Typography variant="subtitle2" color="textSecondary">BILLED TO</Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{user?.name || user?.phone || 'N/A'}</Typography>
          <Typography variant="body2">{user?.phone}</Typography>
          <Typography variant="body2">{user?.email || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={6} sx={{ textAlign: 'right' }}>
          <Typography variant="subtitle2" color="textSecondary">INVOICE DETAILS</Typography>
          <Typography variant="body2">Invoice No: {transaction?.invoiceNo}</Typography>
          <Typography variant="body2">Category: {transaction?.category}</Typography>
          <Typography variant="body2">Status: {transaction?.status}</Typography>
        </Grid>
      </Grid>

      {/* Transaction Details */}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getCategoryIcon(transaction?.category)}
                  <Typography variant="body1">{transaction?.description}</Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="textSecondary">
                  Service: {transaction?.service}
                </Typography>
                {transaction?.details?.number && (
                  <Typography variant="body2" color="textSecondary">
                    Number: {transaction?.details?.number}
                  </Typography>
                )}
                {transaction?.details?.pnr && (
                  <Typography variant="body2" color="textSecondary">
                    PNR: {transaction?.details?.pnr}
                  </Typography>
                )}
                {transaction?.details?.confirmationNumber && (
                  <Typography variant="body2" color="textSecondary">
                    Booking ID: {transaction?.details?.confirmationNumber}
                  </Typography>
                )}
                {transaction?.details?.transactionId && (
                  <Typography variant="body2" color="textSecondary">
                    Txn ID: {transaction?.details?.transactionId}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Typography variant="h6" sx={{ color: transaction?.type === 'CREDIT' ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>
                  {transaction?.type === 'CREDIT' ? '+' : '-'}₹{transaction?.amount?.toLocaleString()}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* GST Summary */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Box sx={{ width: 300 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2" color="textSecondary">Subtotal:</Typography>
            <Typography variant="body2">{'\u20B9'}{(transaction?.amount)?.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2" color="textSecondary">CGST (9%):</Typography>
            <Typography variant="body2">{'\u20B9'}{(transaction?.amount * 0.09)?.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2" color="textSecondary">SGST (9%):</Typography>
            <Typography variant="body2">{'\u20B9'}{(transaction?.amount * 0.09)?.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderTop: '1px solid #ddd', mt: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Total (incl. GST):</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: transaction?.type === 'CREDIT' ? '#4caf50' : '#f44336' }}>
              {transaction?.type === 'CREDIT' ? '+' : '-'}{'\u20B9'}{(transaction?.amount * 1.18)?.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Additional Details for Bookings */}
      {(transaction?.category === 'FLIGHT' || transaction?.category === 'HOTEL') && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Booking Details</Typography>
          {transaction?.category === 'FLIGHT' && transaction?.details && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">Origin: {transaction.details.originCity || transaction.details.origin}</Typography>
                <Typography variant="body2">Destination: {transaction.details.destinationCity || transaction.details.destination}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">Departure: {transaction.details.departureDate}</Typography>
                <Typography variant="body2">Trip Type: {transaction.details.tripType}</Typography>
              </Grid>
              {transaction.details.passengers?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>Passengers:</Typography>
                  {transaction.details.passengers.map((p, i) => (
                    <Typography key={i} variant="body2">{p.title} {p.firstName} {p.lastName}</Typography>
                  ))}
                </Grid>
              )}
            </Grid>
          )}
          {transaction?.category === 'HOTEL' && transaction?.details && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">Hotel: {transaction.details.hotelName}</Typography>
                <Typography variant="body2">City: {transaction.details.hotelCity}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">Check-in: {formatDate(transaction.details.checkIn)}</Typography>
                <Typography variant="body2">Check-out: {formatDate(transaction.details.checkOut)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2">Rooms: {transaction.details.rooms} | Nights: {transaction.details.nights}</Typography>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #ddd', textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          Thank you for using Bharat Darshan!
        </Typography>
        <Typography variant="body2" color="textSecondary">
          For support, contact: 7070878744 | pr@bharatdarshan.today
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
          User Management
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchUsers} color="primary">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Users" value={stats.totalUsers || stats.total} icon={<Person sx={{ fontSize: 40, color: '#2196f3' }} />} color="#2196f3" onClick={() => { setActiveFilter(activeFilter === '' ? '' : ''); setPage(0); }} active={activeFilter === ''} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="With Wallet Balance" value={stats.usersWithWallet || stats.withWallet} icon={<AccountBalanceWallet sx={{ fontSize: 40, color: '#4caf50' }} />} color="#4caf50" onClick={() => { setActiveFilter(activeFilter === 'withWallet' ? '' : 'withWallet'); setPage(0); }} active={activeFilter === 'withWallet'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Complete Profiles" value={stats.usersWithProfile || stats.withProfile} icon={<CheckCircle sx={{ fontSize: 40, color: '#ff9800' }} />} color="#ff9800" onClick={() => { setActiveFilter(activeFilter === 'withProfile' ? '' : 'withProfile'); setPage(0); }} active={activeFilter === 'withProfile'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Today's Signups" value={stats.todaySignups} icon={<Today sx={{ fontSize: 40, color: '#9c27b0' }} />} color="#9c27b0" onClick={() => { setActiveFilter(activeFilter === 'todaySignups' ? '' : 'todaySignups'); setPage(0); }} active={activeFilter === 'todaySignups'} />
        </Grid>
        {stats.deletedUsers > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Deleted Users" value={stats.deletedUsers} icon={<PersonOff sx={{ fontSize: 40, color: '#f44336' }} />} color="#f44336" onClick={() => { setActiveFilter(activeFilter === 'deleted' ? '' : 'deleted'); setPage(0); }} active={activeFilter === 'deleted'} />
          </Grid>
        )}
      </Grid>


      {/* Active Filter Indicator */}
      {activeFilter && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList color="primary" />
          <Chip 
            label={`Filtered: ${activeFilter === 'withWallet' ? 'With Wallet Balance' : activeFilter === 'withProfile' ? 'Complete Profiles' : activeFilter === 'todaySignups' ? "Today's Signups" : activeFilter === 'deleted' ? 'Deleted Users' : 'All'}`}
            color="primary"
            onDelete={() => { setActiveFilter(''); setPage(0); }}
          />
        </Box>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth 
          placeholder="Search by name, mobile, or email..."
          value={search} 
          onChange={handleSearchChange}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          size="small"
        />
      </Paper>

      {/* Users Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Profile</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Mobile</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Wallet</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Milan Sathi</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Joined</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5 }}>No users found</TableCell></TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user._id} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0f7ff' }, ...(user.isDeleted ? { opacity: 0.6, bgcolor: '#fff0f0' } : {}) }}>
                    <TableCell onClick={() => handleRowClick(user)}>
                      <Avatar src={user.profileImageUrl || ''} sx={{ bgcolor: '#2196f3', width: 36, height: 36 }}>
                        {user.name ? user.name.charAt(0).toUpperCase() : user.firstName ? user.firstName.charAt(0).toUpperCase() : 'U'}
                      </Avatar>
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(user)} sx={{ fontWeight: 500 }}>
                      {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A'}
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(user)}>{user.phone || 'N/A'}</TableCell>
                    <TableCell onClick={() => handleRowClick(user)} sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {user.email || 'N/A'}
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(user)}>
                      <Chip label={`₹${user.wallet || 0}`} size="small" color={user.wallet > 0 ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(user)}>
                      <Chip 
                        label={user.isMilanSathiProfile === 'true' || user.isMilanSathiProfile === true ? 'Yes' : 'No'}
                        size="small" 
                        color={user.isMilanSathiProfile === 'true' || user.isMilanSathiProfile === true ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(user)} sx={{ fontSize: '0.8rem' }}>
                      {formatDate(user.createdAt)}
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(user)}>
                      <Chip 
                        label={user.isDeleted ? (user.deletedBy === 'admin' ? 'Deleted (Admin)' : 'Deleted (Self)') : (user.status || 'active')}
                        size="small" 
                        color={user.isDeleted ? 'error' : user.status === 'active' ? 'success' : user.status === 'inactive' ? 'warning' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleRowClick(user)}><Visibility fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Add/Deduct Wallet">
                        <IconButton size="small" color="primary" onClick={() => { setSelectedUser(user); setWalletDialogOpen(true); }}>
                          <AttachMoney fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div" 
          count={totalUsers}
          rowsPerPage={rowsPerPage} 
          page={page}
          onPageChange={handleChangePage} 
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* User Detail Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f5f5f5' }}>
          <Typography variant="h6">User Details</Typography>
          <IconButton onClick={() => setDialogOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, minHeight: 500 }}>
          {selectedUser && (
            <Box>
              {/* User Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar src={selectedUser.profileImageUrl || ''} sx={{ width: 80, height: 80, mr: 2, bgcolor: '#2196f3' }}>
                  {selectedUser.name ? selectedUser.name.charAt(0).toUpperCase() : 'U'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5">{selectedUser.name || `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || 'N/A'}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip label={selectedUser.isDeleted ? (selectedUser.deletedBy === 'admin' ? 'Deleted (Admin)' : 'Deleted (Self)') : (selectedUser.status || 'active')} size="small" color={selectedUser.isDeleted ? 'error' : selectedUser.status === 'active' ? 'success' : selectedUser.status === 'inactive' ? 'warning' : 'error'} />
                    <Chip label={`Wallet: ₹${selectedUser.wallet || 0}`} size="small" color="primary" />
                    {(selectedUser.isMilanSathiProfile === 'true' || selectedUser.isMilanSathiProfile === true) && (
                      <Chip label="Milan Sathi" size="small" color="secondary" />
                    )}
                  </Box>
                </Box>
                <Box>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<AttachMoney />}
                    onClick={() => setWalletDialogOpen(true)}
                    sx={{ mr: 1 }}
                  >
                    Wallet
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="error" 
                    startIcon={<Delete />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>

              {/* Tabs */}
              <Tabs value={dialogTab} onChange={(e, v) => setDialogTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tab label="Basic Info" />
                <Tab label="Milan Sathi Profile" />
                <Tab label={<Badge badgeContent={comprehensiveHistory?.transactions?.length || 0} color="primary">Wallet & Transactions</Badge>} />
                <Tab label="Status Control" />
              </Tabs>

              {/* Tab 0: Basic Info */}
              {dialogTab === 0 && (
                <Grid container spacing={2}>
                  {[
                    { label: 'Mobile', value: selectedUser.phone, editable: true },
                    { label: 'Email', value: selectedUser.email },
                    { label: 'Gender', value: selectedUser.gender },
                    { label: 'Age', value: selectedUser.age },
                    { label: 'City', value: selectedUser.city },
                    { label: 'State', value: selectedUser.state },
                    { label: 'Country', value: selectedUser.country },
                    { label: 'Pincode', value: selectedUser.pincode },
                    { label: 'Wallet Balance', value: `₹${selectedUser.wallet || 0}` },
                    { label: 'Joined', value: formatDate(selectedUser.createdAt) },
                    { label: 'Last Updated', value: formatDate(selectedUser.updatedAt) },
                    { label: 'Country Code', value: selectedUser.countryCode },
                  ].map((item, i) => (
                    <Grid item xs={6} md={4} key={i}>
                      <Typography variant="body2" color="textSecondary">{item.label}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{item.value || 'N/A'}</Typography>
                        {item.editable && (
                          <IconButton size="small" color="primary" onClick={() => { setEditPhoneValue(selectedUser.phone || ''); setPhoneEditOpen(true); }} title="Edit Phone Number">
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Tab 1: Milan Sathi Profile */}
              {dialogTab === 1 && (
                <Grid container spacing={2}>
                  {[
                    { label: 'Religion', value: selectedUser.religion },
                    { label: 'Caste', value: selectedUser.caste },
                    { label: 'Mother Tongue', value: selectedUser.motherTongue },
                    { label: 'Education', value: selectedUser.education },
                    { label: 'Profession', value: selectedUser.profession },
                    { label: 'Income', value: selectedUser.income },
                    { label: 'Marital Status', value: selectedUser.maritalStatus },
                    { label: 'Height', value: selectedUser.height ? `${selectedUser.height} cm` : 'N/A' },
                    { label: 'Diet', value: selectedUser.diet },
                    { label: 'Is Verified', value: selectedUser.isVerified ? 'Yes' : 'No' },
                    { label: 'Document Verified', value: selectedUser.isDocumentVerified ? 'Yes' : 'No' },
                    { label: 'Is Premium', value: selectedUser.isPremium ? 'Yes' : 'No' },
                    { label: 'Plan Name', value: selectedUser.planName },
                    { label: 'Connection Limit', value: selectedUser.connectionLimit },
                    { label: 'Connections Used', value: selectedUser.connectionsUsed },
                  ].map((item, i) => (
                    <Grid item xs={6} md={4} key={i}>
                      <Typography variant="body2" color="textSecondary">{item.label}</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{item.value || 'N/A'}</Typography>
                    </Grid>
                  ))}
                  {selectedUser.bio && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Bio</Typography>
                      <Typography variant="body1">{selectedUser.bio}</Typography>
                    </Grid>
                  )}
                  {selectedUser.hobbies?.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Hobbies</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        {selectedUser.hobbies.map((hobby, i) => (
                          <Chip key={i} label={hobby} size="small" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  {selectedUser.interests?.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Interests</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        {selectedUser.interests.map((interest, i) => (
                          <Chip key={i} label={interest} size="small" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Tab 2: Comprehensive Wallet & Transaction History */}
              {dialogTab === 2 && (
                <Box>
                  {/* Summary Cards */}
                  {comprehensiveHistory && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6} md={2}>
                        <Card sx={{ bgcolor: '#e8f5e9', textAlign: 'center', py: 1 }}>
                          <Typography variant="h6" sx={{ color: '#4caf50' }}>₹{comprehensiveHistory.summary?.totalWalletCredits?.toLocaleString() || 0}</Typography>
                          <Typography variant="caption">Total Credits</Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Card sx={{ bgcolor: '#ffebee', textAlign: 'center', py: 1 }}>
                          <Typography variant="h6" sx={{ color: '#f44336' }}>₹{comprehensiveHistory.summary?.totalWalletDebits?.toLocaleString() || 0}</Typography>
                          <Typography variant="caption">Total Debits</Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Card sx={{ bgcolor: '#e3f2fd', textAlign: 'center', py: 1 }}>
                          <Typography variant="h6" sx={{ color: '#2196f3' }}>₹{comprehensiveHistory.summary?.totalRecharges?.toLocaleString() || 0}</Typography>
                          <Typography variant="caption">Recharges</Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Card sx={{ bgcolor: '#fff3e0', textAlign: 'center', py: 1 }}>
                          <Typography variant="h6" sx={{ color: '#ff9800' }}>₹{comprehensiveHistory.summary?.totalFlightBookings?.toLocaleString() || 0}</Typography>
                          <Typography variant="caption">Flights</Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Card sx={{ bgcolor: '#f3e5f5', textAlign: 'center', py: 1 }}>
                          <Typography variant="h6" sx={{ color: '#9c27b0' }}>₹{comprehensiveHistory.summary?.totalHotelBookings?.toLocaleString() || 0}</Typography>
                          <Typography variant="caption">Hotels</Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Card sx={{ bgcolor: '#e0f2f1', textAlign: 'center', py: 1 }}>
                          <Typography variant="h6" sx={{ color: '#009688', fontWeight: 'bold' }}>₹{comprehensiveHistory.summary?.currentBalance?.toLocaleString() || 0}</Typography>
                          <Typography variant="caption">Current Balance</Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  )}

                  {/* Filter */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Filter Transactions</InputLabel>
                      <Select value={historyFilter} onChange={handleFilterChange} label="Filter Transactions">
                        <MenuItem value="all">All Transactions</MenuItem>
                        <MenuItem value="wallet">Wallet Only</MenuItem>
                        <MenuItem value="recharge">Recharges Only</MenuItem>
                        <MenuItem value="flight">Flights Only</MenuItem>
                        <MenuItem value="hotel">Hotels Only</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="textSecondary">
                      Total: {comprehensiveHistory?.pagination?.total || 0} transactions
                    </Typography>
                  </Box>

                  {/* Transaction List */}
                  {historyLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                  ) : !comprehensiveHistory?.transactions?.length ? (
                    <Typography color="textSecondary" align="center" sx={{ py: 4 }}>No transactions found</Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }} align="right">Amount</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Invoice</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {comprehensiveHistory.transactions.map((tx, i) => (
                            <TableRow key={i} hover>
                              <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(tx.date)}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {getCategoryIcon(tx.category)}
                                  <Typography variant="body2">{tx.category}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{tx.description}</Typography>
                                {tx.details?.number && (
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    {tx.service}: {tx.details.number}
                                  </Typography>
                                )}
                                {tx.details?.pnr && (
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    PNR: {tx.details.pnr}
                                  </Typography>
                                )}
                                {tx.details?.confirmationNumber && (
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    Booking: {tx.details.confirmationNumber}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip label={tx.status} size="small" color={getStatusColor(tx.status)} />
                              </TableCell>
                              <TableCell align="right">
                                <Typography sx={{ color: tx.type === 'CREDIT' ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>
                                  {tx.type === 'CREDIT' ? '+' : '-'}₹{tx.amount?.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Tooltip title="View Invoice">
                                  <IconButton size="small" onClick={() => handleShowInvoice(tx)} color="primary">
                                    <Receipt fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {/* Tab 3: Status Control */}
              {dialogTab === 3 && (
                <Box>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    Changing user status will affect their ability to use the app. Deleting a user will permanently remove all their data.
                  </Alert>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Card sx={{ p: 2, textAlign: 'center', border: selectedUser.status === 'active' ? '2px solid #4caf50' : '1px solid #ddd' }}>
                        <PersonOutline sx={{ fontSize: 48, color: '#4caf50' }} />
                        <Typography variant="h6">Active</Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>User can access all features</Typography>
                        <Button 
                          variant={selectedUser.status === 'active' ? 'contained' : 'outlined'}
                          color="success"
                          onClick={() => handleStatusUpdate('active')}
                          disabled={statusLoading || selectedUser.status === 'active'}
                        >
                          {selectedUser.status === 'active' ? 'Current Status' : 'Set Active'}
                        </Button>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card sx={{ p: 2, textAlign: 'center', border: selectedUser.status === 'inactive' ? '2px solid #ff9800' : '1px solid #ddd' }}>
                        <PersonOff sx={{ fontSize: 48, color: '#ff9800' }} />
                        <Typography variant="h6">Inactive</Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Milan Sathi profile deactivated (App still accessible)</Typography>
                        <Button 
                          variant={selectedUser.status === 'inactive' ? 'contained' : 'outlined'}
                          color="warning"
                          onClick={() => handleStatusUpdate('inactive')}
                          disabled={statusLoading || selectedUser.status === 'inactive'}
                        >
                          {selectedUser.status === 'inactive' ? 'Current Status' : 'Set Inactive'}
                        </Button>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card sx={{ p: 2, textAlign: 'center', border: '1px solid #ddd' }}>
                        <Delete sx={{ fontSize: 48, color: '#f44336' }} />
                        <Typography variant="h6">Deactivate</Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Soft delete - user marked as deleted</Typography>
                        <Button 
                          variant="outlined"
                          color="error"
                          onClick={() => setDeleteDialogOpen(true)}
                          disabled={deleteLoading}
                        >
                          Delete User
                        </Button>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Wallet Transaction Dialog */}
      <Dialog open={walletDialogOpen} onClose={() => setWalletDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachMoney sx={{ mr: 1 }} />
            Wallet Transaction
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedUser && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Current Balance: <strong>₹{selectedUser.wallet || 0}</strong>
              </Alert>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Transaction Type</InputLabel>
                <Select value={walletType} onChange={(e) => setWalletType(e.target.value)} label="Transaction Type">
                  <MenuItem value="CREDIT">Credit (Add Money)</MenuItem>
                  <MenuItem value="DEBIT">Debit (Deduct Money)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Amount (₹)"
                type="number"
                value={walletAmount}
                onChange={(e) => setWalletAmount(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Transaction For</InputLabel>
                <Select value={walletTransactionFor} onChange={(e) => setWalletTransactionFor(e.target.value)} label="Transaction For">
                  <MenuItem value="WALLET_TOPUP">Wallet Topup</MenuItem>
                  <MenuItem value="MOBILE RECHARGE">Mobile Recharge</MenuItem>
                  <MenuItem value="ELECTRICITY">Electricity</MenuItem>
                  <MenuItem value="ECOMMERCE">E-commerce</MenuItem>
                  <MenuItem value="SERVICE_CHARGE">Service Charge</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Notes (Reason)"
                multiline
                rows={2}
                value={walletNotes}
                onChange={(e) => setWalletNotes(e.target.value)}
                placeholder="Enter reason for this transaction..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setWalletDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color={walletType === 'CREDIT' ? 'success' : 'error'}
            onClick={handleWalletTransaction}
            disabled={walletLoading}
            startIcon={walletLoading ? <CircularProgress size={20} /> : walletType === 'CREDIT' ? <Add /> : <Remove />}
          >
            {walletType === 'CREDIT' ? 'Add Money' : 'Deduct Money'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={invoiceDialogOpen} onClose={() => setInvoiceDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Invoice</Typography>
          <Box>
            <Tooltip title="Print Invoice">
              <IconButton onClick={handlePrintInvoice} color="primary" sx={{ mr: 1 }}>
                <Print />
              </IconButton>
            </Tooltip>
            <IconButton onClick={() => setInvoiceDialogOpen(false)}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTransaction && comprehensiveHistory?.user && (
            <InvoiceContent transaction={selectedTransaction} user={comprehensiveHistory.user} />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: '#f44336' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Warning sx={{ mr: 1 }} />
            Confirm Delete
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 1 }}>
            This action cannot be undone! The following will be permanently deleted:
            <ul>
              <li>User profile and all personal data</li>
              <li>All wallet transaction history</li>
              <li>All booking history</li>
              <li>Milan Sathi profile and connections</li>
            </ul>
          </Alert>
          {selectedUser && (
            <Typography sx={{ mt: 2 }}>
              Are you sure you want to delete user <strong>{selectedUser.name || selectedUser.phone}</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteUser}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <Delete />}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      {/* Phone Edit Dialog */}
      <Dialog open={phoneEditOpen} onClose={() => setPhoneEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Phone Number</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Current: {selectedUser?.phone || 'N/A'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="New Phone Number"
            value={editPhoneValue}
            onChange={(e) => setEditPhoneValue(e.target.value.replace(/[^0-9]/g, ''))}
            inputProps={{ maxLength: 10 }}
            placeholder="Enter 10-digit number"
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            This will update the phone number across all modules (Milan Sathi, Sanatani Manch, etc.)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhoneEditOpen(false)} disabled={phoneEditLoading}>Cancel</Button>
          <Button onClick={handlePhoneUpdate} variant="contained" color="primary" disabled={phoneEditLoading}>
            {phoneEditLoading ? 'Updating...' : 'Update Phone'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #invoice-content, #invoice-content * { visibility: visible; }
          #invoice-content { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </Box>
  );
};

export default NewUsers;
